import { Link } from 'react-router-dom';
import Icon from './AppIcon';
import NewsletterSignup from './NewsletterSignup';
import { CONTACT } from 'config/contact';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const navigation = {
    services: [
      { name: 'n8n & Make Workflows', href: '/n8n-make-com-workflows' },
      { name: 'WhatsApp Automation', href: '/whats-app-email-automation' },
      { name: 'AI Chatbots', href: '/ai-chatbot-automation' },
      { name: 'API Integrations', href: '/custom-api-integrations' },
      { name: 'SEO Services', href: '/seo-services' },
      { name: 'Local SEO', href: '/local-seo' },
      { name: 'Social Media Marketing', href: '/social-media-marketing' },
      { name: 'Google Ads & PPC', href: '/google-ads-ppc' },
      { name: 'Meta Ads', href: '/meta-ads' },
      { name: 'Content Marketing', href: '/content-marketing' },
      { name: 'Email Marketing', href: '/email-marketing' },
      { name: 'Analytics & CRO', href: '/analytics-and-cro' }
    ],
    company: [
      { name: 'About Us', href: '/about-us' },
      { name: 'Case Studies', href: '/case-studies' },
      { name: 'Pricing', href: '/pricing' },
      { name: 'Contact', href: '/contact' }
    ],
    legal: [
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Cookie Policy', href: '/cookies' }
    ]
  };

  // Only verified profiles are linked. Add real social handles here when available.
  const socialLinks = [
    { name: 'GitHub', href: 'https://github.com/pk468080', icon: 'Github' }
  ];

  // use centralized contact constants
  const contactInfo = {
    phone: CONTACT.displayPhone,
    email: CONTACT.email,
    location: CONTACT.address,
    whatsapp: CONTACT.whatsappNumber,
    telLink: CONTACT.telLink,
    whatsappLink: CONTACT.whatsappLink
  };

  return (
    <footer className="bg-background border-t border-border pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          
          {/* Column 1: Brand, Contact & Newsletter (Spans 4 cols) */}
          <div className="lg:col-span-4 space-y-8">
            {/* Brand */}
            <div className="space-y-4">
              <Link to="/" className="inline-flex items-center space-x-2">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center text-white">
                  <Icon name="Zap" size={20} />
                </div>
                <span className="text-xl font-bold text-foreground">
                  Make Mind
                </span>
              </Link>
              <p className="text-sm text-muted-foreground leading-relaxed max-w-xs">
                Automation that thinks like you. We help businesses reclaim 40+ hours a week through intelligent workflows and AI agents.
              </p>
            </div>

            {/* Contact Details */}
            <div className="space-y-3">
              <a href={contactInfo.telLink} className="flex items-center space-x-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                <Icon name="Phone" size={16} />
                <span>{contactInfo.phone}</span>
              </a>
              <a href={`mailto:${contactInfo.email}`} className="flex items-center space-x-3 text-sm text-muted-foreground hover:text-primary transition-colors">
                <Icon name="Mail" size={16} />
                <span>{contactInfo.email}</span>
              </a>
              <div className="flex items-center space-x-3 text-sm text-muted-foreground">
                <Icon name="MapPin" size={16} />
                <span>{contactInfo.location}</span>
              </div>
            </div>

            {/* Newsletter Component */}
            <div className="pt-4">
               <NewsletterSignup variant="footer" />
            </div>
          </div>

          {/* Spacer Column */}
          <div className="hidden lg:block lg:col-span-1" />

          {/* Column 2: Links (Spans 7 cols) */}
          <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-3 gap-8">
            
            {/* Services */}
            <div>
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-6">
                Solutions
              </h3>
              <ul className="space-y-4">
                {navigation.services.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Company */}
            <div>
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-6">
                Company
              </h3>
              <ul className="space-y-4">
                {navigation.company.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h3 className="text-sm font-bold text-foreground uppercase tracking-wider mb-6">
                Legal
              </h3>
              <ul className="space-y-4">
                {navigation.legal.map((item) => (
                  <li key={item.name}>
                    <Link
                      to={item.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors duration-200"
                    >
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground text-center md:text-left">
            © {currentYear} {CONTACT.businessName}. All rights reserved.
          </p>

          {/* Social Icons */}
          <div className="flex items-center space-x-4">
            {socialLinks.map((item) => (
              <a
                key={item.name}
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-white transition-all duration-300"
                aria-label={item.name}
              >
                <Icon name={item.icon} size={16} />
              </a>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;